import React, { useContext, useEffect, useState, useMemo, memo } from "react"
import { Icon } from "@iconify/react"
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext"
import { useTheme } from "@mui/styles"
import { AuthContext } from "../../context/Auth/AuthContext"

// Componente IconComponent memoizado para evitar re-renderizações desnecessárias
const IconComponent = memo(({ icon = "", color }) => {
    const theme = useTheme()
    const finalColor = color || theme.palette.sideMenuTextColor

    return (
        <Icon
            icon={icon}
            width={24}
            color={finalColor}
            // Adicionando key para estabilidade de renderização
            key={`icon-${icon}-${finalColor}`}
        />
    )
})

// Definir displayName explicitamente para debugging
IconComponent.displayName = "IconComponent"

const useNavItems = () => {
    const theme = useTheme()
    const { whatsApps } = useContext(WhatsAppsContext)
    const { user } = useContext(AuthContext)

    const [plan, setPlan] = useState({})

    const [connectionWarning, setConnectionWarning] = useState(false)
    const [hasDefaultWhatsApp, setHasDefaultWhatsApp] = useState(false)

    const enableAutomation =
        localStorage.getItem("enableAutomations") && user.profile === "admin"

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            if (whatsApps.length > 0) {
                const offlineWhats = whatsApps.filter((whats) => {
                    return (
                        whats.status === "qrcode" ||
                        whats.status === "PAIRING" ||
                        whats.status === "DISCONNECTED" ||
                        whats.status === "TIMEOUT" ||
                        whats.status === "OPENING"
                    )
                })

                const defaultWhatsApp = whatsApps.filter(
                    (whats) => whats.isDefault && whats.status === "CONNECTED",
                )

                setHasDefaultWhatsApp(defaultWhatsApp.length > 0)
                setConnectionWarning(offlineWhats.length > 0)
            }
        }, 2000)
        return () => clearTimeout(delayDebounceFn)
    }, [whatsApps?.length])

    useEffect(() => {
        if (user.company?.subscriptions?.length) {
            setPlan(user.company.subscriptions?.[0]?.plan || {})
        }
    }, [user.company?.subscriptions])

    const commonIcons = useMemo(() => {
        return {
            home: <IconComponent icon="famicons:home-outline" />,
            whatsapp: <IconComponent icon="ic:outline-whatsapp" />,
            contacts: <IconComponent icon="fluent:book-contacts-20-regular" />,
            queues: <IconComponent icon="heroicons:queue-list-solid" />,
            wifiOff: <IconComponent icon="ic:round-wifi-off" />,
            wifiOn: (
                <IconComponent icon="material-symbols:wifi" color="green" />
            ),
            kanban: <IconComponent icon="teenyicons:kanban-outline" />,
            products: <IconComponent icon="icon-park-outline:weixin-market" />,
            forms: <IconComponent icon="clarity:form-line" />,
            leads: <IconComponent icon="mdi:leads-outline" />,
            tags: <IconComponent icon="lucide:tag" />,
            ai: <IconComponent icon="hugeicons:brain-02" />,
            quickMessages: (
                <IconComponent icon="material-symbols:quick-phrases" />
            ),
            schedules: <IconComponent icon="akar-icons:schedule" />,
            integrations: (
                <IconComponent icon="carbon:power-enterprise-pools-metered-capacity-integration" />
            ),
            audios: <IconComponent icon="ant-design:audio-outlined" />,
            campaigns: <IconComponent icon="iconoir:megaphone" />,
            list: <IconComponent icon="iconoir:list" />,
            contactBook: <IconComponent icon="hugeicons:contact-book" />,
            settings: <IconComponent icon="carbon:settings" />,
            todo: <IconComponent icon="pajamas:todo-done" />,
            chat: <IconComponent icon="token:chat" />,
            announcements: (
                <IconComponent icon="icon-park-outline:announcement" />
            ),
            generalSettings: <IconComponent icon="ic:outline-settings" />,
            users: <IconComponent icon="lucide:users" />,
            financial: <IconComponent icon="formkit:dollar" />,
            api: <IconComponent icon="mdi:api" />,
            help: <IconComponent icon="material-symbols:help-outline" />,
            prompts: (
                <IconComponent icon="material-symbols-light:text-ad-outline" />
            ),
            suggestions: <IconComponent icon="iconoir:suggestion" />,
            autoDM: <IconComponent icon="mdi:instagram" />,
        }
    }, [theme.palette.sideMenuTextColor])

    const isAdmin = user.profile === "admin"
    const isSuper = user.super

    const navItems = useMemo(
        () => [
            {
                subtitle: "Home",
                items: [
                    {
                        name: "Inicio",
                        path: "/",
                        icon: commonIcons.home,
                        disable: !isAdmin || plan.dashboard === false,
                    },
                    {
                        name: "Atendimento",
                        path: "/tickets",
                        icon: commonIcons.whatsapp,
                    },
                    {
                        name: "Contatos",
                        path: "/contacts",
                        icon: commonIcons.contacts,
                    },
                    {
                        name: "Filas",
                        path: "/queues",
                        icon: commonIcons.queues,
                        disable: !isAdmin,
                    },
                    {
                        name: "Conexões",
                        path: "/connections",
                        disable: !isAdmin,
                        icon: connectionWarning
                            ? commonIcons.wifiOff
                            : commonIcons.wifiOn,
                        badge: {
                            enabled: !!connectionWarning,
                            content: "!",
                            color: "error",
                        },
                    },
                ],
            },
            {
                subtitle: "CRM",
                items: [
                    {
                        name: "Kanban",
                        path: "/kanban",
                        icon: commonIcons.kanban,
                        disable: plan.kanban === false,
                    },
                    {
                        name: "Formulários",
                        path: "/forms",
                        icon: commonIcons.forms,
                        disable: !isAdmin || plan.forms === false,
                    },
                    {
                        name: "Produtos",
                        path: "/products",
                        icon: commonIcons.products,
                        disable: !isAdmin || plan.products === false,
                    },
                    {
                        name: "Leads",
                        path: "/leads",
                        icon: commonIcons.leads,
                        disable: !isAdmin || plan.leads === false,
                    },
                    {
                        name: "Tags",
                        path: "/tags",
                        icon: commonIcons.tags,
                        disable: !isAdmin || plan.tags === false,
                    },
                ],
            },
            {
                subtitle: "Automação",
                items: [
                    {
                        name: "Inteligência Artificial",
                        icon: commonIcons.ai,
                        disable: !isAdmin || plan.prompt === false,
                        children: [
                            {
                                name: "Prompts de atendimento",
                                icon: commonIcons.prompts,
                                path: "/prompts",
                                disable: !isAdmin || plan.prompt === false,
                            },
                            {
                                name: "Sugestões",
                                icon: commonIcons.suggestions,
                                path: "/suggestions",
                                disable:
                                    !localStorage.getItem("sugenabled") ||
                                    !isAdmin ||
                                    plan.prompt === false,
                            },
                        ],
                    },
                    {
                        name: "Respostas rápidas",
                        path: "/quick-messages",
                        icon: commonIcons.quickMessages,
                        disable: plan?.["quick-messages"] === false,
                    },
                    {
                        name: "Mensagens programadas",
                        path: "/schedules",
                        icon: commonIcons.schedules,
                        disable: plan.schedules === false,
                    },

                    {
                        name: "Integrações",
                        path: "/queue-integration",
                        icon: commonIcons.integrations,
                        disable: !isAdmin || plan.queueIntegration === false,
                    },
                    {
                        name: "Áudios",
                        path: "/audios",
                        icon: commonIcons.audios,
                        disable: plan.audios === false,
                    },
                    {
                        name: "Instagram",
                        path: "/dm-automations",
                        icon: commonIcons.autoDM,
                        disable: !enableAutomation || plan.automation === false,
                    },
                    {
                        name: "Campanhas",
                        icon: commonIcons.campaigns,
                        disable:
                            !hasDefaultWhatsApp ||
                            !isAdmin ||
                            plan.campaigns === false,
                        // disable: companyId !== 2,
                        children: [
                            {
                                name: "Ver todas",
                                path: "/campaigns",
                                icon: commonIcons.list,
                            },
                            {
                                name: "Listas de contatos",
                                path: "/contact-lists",
                                icon: commonIcons.contactBook,
                            },
                            {
                                name: "Configurações",
                                path: "/campaigns-config",
                                icon: commonIcons.settings,
                            },
                        ],
                    },
                ],
            },

            {
                subtitle: "Ferramentas",
                items: [
                    {
                        name: "Tarefas",
                        path: "/todolist",
                        icon: commonIcons.todo,
                    },
                    {
                        name: "Chat interno",
                        path: "/chats",
                        icon: commonIcons.chat,
                        disable: plan.chats === false,
                    },
                    {
                        name: "Informativos",
                        path: "/announcements",
                        icon: commonIcons.announcements,
                        disable: !isSuper,
                    },
                ],
            },
            {
                subtitle: "Configurações",
                items: [
                    {
                        name: "Opções",
                        path: "/settings",
                        icon: commonIcons.generalSettings,
                        disable: !isAdmin,
                    },
                    {
                        name: "Usuários",
                        path: "/users",
                        icon: commonIcons.users,
                        disable: !isAdmin,
                    },
                    {
                        name: "Financeiro",
                        path: "/financeiro",
                        icon: commonIcons.financial,
                        disable: !isAdmin,
                    },
                    {
                        name: "API",
                        path: "/messages-api",
                        icon: commonIcons.api,
                        disable: !isAdmin,
                    },
                    {
                        name: "Ajuda",
                        path: "/helps",
                        icon: commonIcons.help,
                    },
                ],
            },
        ],
        [commonIcons, connectionWarning, hasDefaultWhatsApp, plan],
    )

    return {
        navItems,
    }
}

export default useNavItems
